// src/pages/RoleList.tsx
import React, { useEffect, useState } from "react";
import { Avatar, Descriptions, Spin, Tag } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";
import CommonModal from "../../components/CommonModal";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../app/store";
import { deleteRoleById, fetchAllRoles } from "../../app/roleSlice";
import { Role } from "../../types/roleTypes";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../../utils/permission";
import { useDebounce } from "use-debounce";
import Swal from "sweetalert2";
import SortIcons from "../../components/SortIcons/SortIcons";

const RoleList = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { total } = useSelector((state: RootState) => state.role);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [pagesize] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);
  const [roleList, setRoleList] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState<"id" | "display_name" | "status">(
    "id"
  );
  const [orderDir, setOrderDir] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    const loadRoles = async () => {
      try {
        setLoading(true);
        const result = await dispatch(
          fetchAllRoles({
            page,
            pagesize,
            search: debouncedSearch,
            order_by: orderBy,
            order_dir: orderDir,
          })
        ).unwrap();
        setRoleList(result.data?.data || []);
      } catch (err: any) {
        toast.error(err.message || "Failed to fetch roles");
      } finally {
        setLoading(false);
      }
    };

    loadRoles();
  }, [dispatch, page, pagesize, debouncedSearch, orderBy, orderDir]);
  const handleSort = (field: typeof orderBy) => {
    if (orderBy === field) {
      setOrderDir((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setOrderBy(field);
      setOrderDir("desc");
    }
    setPage(1);
  };

  const handleDelete = async (id: number) => {
    try {
      const result = await dispatch(deleteRoleById(id)).unwrap();
      setRoleList((prevList) => prevList.filter((role) => role.id !== id));
      setPage(1);
      return result.message;
    } catch (error: any) {
      toast.error(error || "Failed to delete role");
    }
  };

  return (
    <>
      <Breadcrumb pageName="Manage Role" />
      <div className="rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-md dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">
        <div className="max-w-full overflow-x-auto">
          <div className="flex justify-end mb-4">
            <input
              type="text"
              placeholder="Search by role name"
              value={search}
              onChange={(e) => {
                const value = e.target.value.replace(/^\s+/, "");
                setSearch(value);
                setPage(1);
              }}
              className="w-full sm:w-1/3 rounded-md border border-gray-300 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring focus:border-blue-500"
            />
          </div>

          <table className="w-full table-auto sm:table-fixed">
            <thead>
              <tr className="bg-gray-2 text-left dark:bg-meta-4">
                <th className="py-4 px-4 text-left">Sr No</th>
                <th
                  className="py-4 px-4 text-left cursor-pointer"
                  onClick={() => handleSort("display_name")}
                >
                  <div className="flex items-center gap-2">
                    <span>Role Name</span>
                    <SortIcons
                      isActive={orderBy === "display_name"}
                      direction={orderDir}
                    />
                  </div>
                </th>
                <th className="py-4 px-4 text-left">Status</th>
                <th className="py-4 px-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-6">
                    <Spin />
                  </td>
                </tr>
              ) : roleList.length > 0 ? (
                roleList?.map((role: Role, index: number) => (
                  <tr key={role.id}>
                    <td className="border-b py-4 px-4">
                      {(page - 1) * pagesize + index + 1}
                    </td>
                    <td className="border-b py-4 px-4"
                      style={{ whiteSpace: "pre" }}
                    >
                      {role.display_name?.replace(/\\\//g, "/")}
                    </td>
                    <td className="border-b py-4 px-4">
                      <Tag
                        color={
                          role.status?.toLowerCase() === "active"
                            ? "green"
                            : role.status?.toLowerCase() === "inactive"
                            ? "red"
                            : "default"
                        }
                        className="uppercase font-medium"
                      >
                        {role.status ? role.status.toUpperCase() : "--"}
                      </Tag>
                    </td>
                    <td className="border-b py-4 px-4">
                      <div className="flex items-center gap-3">
                        {hasPermission("role-view") && (
                          <button
                            onClick={() => {
                              setSelectedRole(role);
                              setIsModalVisible(true);
                            }}
                            className="hover:text-primary text-base"
                          >
                            <EyeOutlined />
                          </button>
                        )}
                        {hasPermission("role-update") && (
                          <button
                            className="hover:text-green-600 text-base"
                            onClick={() =>
                              navigate("/dashboard/updaterole", {
                                state: { role },
                              })
                            }
                          >
                            <EditOutlined />
                          </button>
                        )}
                        {hasPermission("role-delete") && (
                          <button
                            className="hover:text-red-500 text-base"
                            onClick={async () => {
                              const result = await Swal.fire({
                                title:
                                  "Are you sure you want to delete this role?",
                                text: "You won't be able to revert this!",
                                icon: "warning",
                                showCancelButton: true,
                                confirmButtonColor: "#d33",
                                cancelButtonColor: "#3085d6",
                                confirmButtonText: "Yes, delete it!",
                              });

                              if (result.isConfirmed) {
                                try {
                                  const message = await handleDelete(role.id);
                                  await Swal.fire({
                                    title: "Deleted!",
                                    text: message || "Role has been deleted.",
                                    icon: "success",
                                    timer: 1500,
                                    showConfirmButton: false,
                                  });
                                } catch (err: any) {
                                  await Swal.fire({
                                    title: "Error",
                                    text:
                                      err?.message || "Failed to delete role",
                                    icon: "error",
                                  });
                                }
                              }
                            }}
                          >
                            <DeleteOutlined />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center font-medium py-4">
                    No result found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {roleList.length > 0 && (
            <div className="flex justify-between items-center mt-4 mb-2 flex-wrap gap-2">
              <div className="text-sm text-gray-600">
                Showing {(page - 1) * pagesize + 1} to{" "}
                {Math.min(page * pagesize, total)} of {total} roles
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                {/* Prev */}
                <button
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    page === 1
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "hover:bg-blue-100 text-blue-600 border-blue-300"
                  }`}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                >
                  &lt;
                </button>

                {/* Page Numbers with Ellipsis */}
                {[...Array(Math.ceil(total / pagesize)).keys()]
                  .map((n: number) => n + 1)
                  .filter((p: number) => {
                    return (
                      p === 1 ||
                      p === Math.ceil(total / pagesize) ||
                      (p >= page - 1 && p <= page + 1)
                    );
                  })
                  .reduce((acc: (number | string)[], val: number, i, arr) => {
                    if (i > 0 && val - (arr[i - 1] as number) > 1) {
                      acc.push("...");
                    }
                    acc.push(val);
                    return acc;
                  }, [])
                  .map((p: number | string, idx: number) =>
                    typeof p === "string" ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-3 py-1.5 text-sm text-gray-500"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`px-3 py-1.5 text-sm rounded-md border ${
                          p === page
                            ? "bg-gray-300 text-gray-800"
                            : "hover:bg-blue-100 text-blue-600 border-blue-300"
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                {/* Next */}
                <button
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    page * pagesize >= total
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "hover:bg-blue-100 text-blue-600 border-blue-300"
                  }`}
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={page * pagesize >= total}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>

        <CommonModal
          visible={isModalVisible}
          onClose={() => {
            setIsModalVisible(false);
            setSelectedRole(null);
          }}
          title={
            <div className="flex items-center gap-2">
              <Avatar icon={<UserSwitchOutlined />} />
              <span className="font-semibold text-gray-800">Role Details</span>
            </div>
          }
        >
          {!selectedRole ? (
            <div className="h-32 flex justify-center items-center">
              <Spin />
            </div>
          ) : (
            <Descriptions
              column={1}
              bordered
              size="middle"
              labelStyle={{
                fontWeight: 600,
                width: "140px",
                background: "#f9f9f9",
              }}
              contentStyle={{ backgroundColor: "#fff" }}
            >
              <Descriptions.Item label="Role Name">
                {selectedRole.display_name}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={selectedRole.status === "active" ? "green" : "red"}>
                  {selectedRole.status.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Permissions">
                <div className="flex flex-wrap gap-2">
                  {selectedRole.permissions?.map((perm) => (
                    <Tag key={perm.id}>{perm.display_name}</Tag>
                  ))}
                </div>
              </Descriptions.Item>
            </Descriptions>
          )}
        </CommonModal>
      </div>
    </>
  );
};

export default RoleList;
